import Env from 'lib/Env';
import UsageService from 'lib/UsageWrapper';
import { USAGE_TAGS } from 'lib/constants';
import GameDisplayNames from 'a/hub/data/GameDisplayNames';
import { SEASON } from 'a/hub/data/constants';

const AppSessionActionTypes = {
  RESET_SESSION_TIMER: 'RESET_SESSION_TIMER',
  FIRST_USER_INTERACTION: 'FIRST_USER_INTERACTION',
  SET_INITIAL_HUB_LOADED: 'SET_INITIAL_HUB_LOADED',
  SET_PRODUCT: 'SET_PRODUCT',
  UPDATE_USAGE_TAGS: 'UPDATE_USAGE_TAGS',
  SET_SHOW_GAME_PROTOTYPES: 'SET_SHOW_GAME_PROTOTYPES',
};

const resetSessionTimer = () => ({ type: AppSessionActionTypes.RESET_SESSION_TIMER });

const firstUserInteraction = (isFirstInteraction = true) => ({
  type: AppSessionActionTypes.FIRST_USER_INTERACTION,
  payload: isFirstInteraction,
});

const setInitialHubLoaded = () => ({ type: AppSessionActionTypes.SET_INITIAL_HUB_LOADED });

const setProduct = product => ({
  type: AppSessionActionTypes.SET_PRODUCT,
  payload: {
    product,
  },
});

/**
 * Compares current tags for Heartbeat RTU (Real Time Usage).
 * Updates tags to Heartbeat and dispatches changes to redux for tracking.
 * @param {object} tagsToUpdate an object for any new or added usage tag
 */
const updateUsageTags = (tagsToUpdate = {}) => (dispatch, getState) => {
  const {
    appSessionReducer: { product, usageTags: existingTags },
    persistStateReducer: { season },
  } = getState();
  const updatedTags = {}; // for updating redux
  let hasChanges = false; // determine if need to call Usage service

  const gameDisplayName = GameDisplayNames[product]?.gameDisplayNames ?? {};
  // Normalize tagging values within Metagame
  const metagameValues = new Set(['Quest', 'Hub', 'hub', 'Map', 'workshop']);
  const backpackValue = 'Curioso'; // Seasons 1&2
  const progressPageValues = new Set(['PracticePage', 'questProgressScreen']); // Season 3

  // Track any tags that have changed values and update formatting where applicable
  Object.entries(tagsToUpdate).forEach(([key, value]) => {
    const existingTag = existingTags[key];
    let updatedTag = value;

    // Update formatting if game display name - can apply to multiple tags, eg, location, assessment
    updatedTag = gameDisplayName[value] ?? value;

    // Group metagame locations
    if (key === USAGE_TAGS.LOCATION.prefix) {
      if (metagameValues.has(value)) {
        updatedTag = USAGE_TAGS.LOCATION.value.METAGAME;
      } else if (value === backpackValue && [SEASON.GRADE_K_1, SEASON.GRADE_2_3].includes(season)) {
        updatedTag = USAGE_TAGS.LOCATION.value.BACKPACK;
      } else if (progressPageValues.has(value) && season === SEASON.GRADE_4_5) {
        updatedTag = USAGE_TAGS.LOCATION.value.PROGRESS;
      }
    }
    if (existingTag !== updatedTag) {
      hasChanges = true;
      updatedTags[key] = updatedTag;
    }
  });

  // Update tags if there are changes
  if (hasChanges) {
    // Merge existing and updated tags and determine whether to call Usage service
    // object for redux; array for Usage service
    const finalTags = { ...existingTags, ...updatedTags };

    // Only call setTags if there are tag changes and a valid location or assessment
    if (finalTags[USAGE_TAGS.LOCATION.prefix] || finalTags[USAGE_TAGS.ASSESSMENT.prefix]) {
      // Transform tags into an array of formatted strings
      const usageServiceTagValues = Object.entries(finalTags).reduce(
        (acc, [key, value]) => {
          if (value !== null) {
            acc.push(`${key}: ${value}`);
          }
          return acc;
        }, [],
      );

      UsageService.setTags(usageServiceTagValues);
      // Add all current tags to AQUA_GLOBALS to easily verify
      if (Env.isDevEnv) {
        AQUA_GLOBALS.USAGE_TAGS = usageServiceTagValues;
      }
    }

    // Update redux to track all tags
    dispatch({
      type: AppSessionActionTypes.UPDATE_USAGE_TAGS,
      payload: finalTags,
    });
  }
};

const setShowGamePrototypes = (value) => ({
  type: AppSessionActionTypes.SET_SHOW_GAME_PROTOTYPES,
  payload: value,
});

export {
  AppSessionActionTypes,
  resetSessionTimer,
  firstUserInteraction,
  setInitialHubLoaded,
  setProduct,
  updateUsageTags,
  setShowGamePrototypes,
};
